import { lazy, Suspense, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, InputSelect, InputTypeahead } from '@farmshare/ui-components';
import { FormikProvider, useFormikContext } from 'formik';
import { useRecoilValue } from 'recoil';
import { userState, vendorState } from 'state';
import { parsePhoneNumber } from 'awesome-phonenumber';

import { EnumUserType, User, useStateManyQuery } from 'lib/graphql';

import {
  extractPhoneDigits,
  formatPhoneDisplay,
  isPhoneNumberValid,
} from '../../helpers/formatting';
import { getUserTypeOptions } from '../../helpers/customer-form-helpers';
import { useCustomerOptions } from '../../hooks/use-customer-options';
import { useCustomerForm } from '../../hooks/use-customer-form';
import type { CustomerFormValues } from '../../helpers/customer-validation';

// Helper component to handle nested producer dialog
// This is defined here to avoid circular dependency issues
function NestedProducerDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (created: User) => void | Promise<void>;
}) {
  // Lazy import to avoid circular dependency
  const AddCustomerDialog = lazy(
    () => import('../add-customer-dialog/add-customer-dialog'),
  );

  return (
    <Suspense fallback={null}>
      <AddCustomerDialog
        key="add-producer-dialog"
        open={open}
        onClose={onClose}
        titleOverride="Add Producer"
        forcedType={EnumUserType.Producer}
        onSuccess={onSuccess}
      />
    </Suspense>
  );
}

export interface CustomerFormProps {
  customer?: User | null;
  forcedType?: EnumUserType;
  onSuccess?: (created: User) => void | Promise<void> | undefined;
  onCancel: () => void;
  /** Called after successful save - defaults to onCancel if not provided */
  onClose?: () => void;
  /** Whether the form is being used inside a dialog (affects producer options loading) */
  isInDialog?: boolean;
  /** Show action buttons (Save/Cancel) */
  showActions?: boolean;
  /** Custom action buttons to render instead of default Save/Cancel */
  customActions?: React.ReactNode;
  /** ID prefix for form fields (useful when multiple forms on same page) */
  idPrefix?: string;
  /** Render as div instead of form element (use when nested in another form) */
  renderAsDiv?: boolean;
  /** Hide the address section entirely */
  hideAddressSection?: boolean;
  /** When true, do not upsert on save; instead return validated values */
  deferMode?: boolean;
  /** Callback when deferMode is true and form is saved with valid values */
  onDeferSubmit?: (vals: CustomerFormValues) => void | Promise<void>;
  /** Optional context to tweak form behavior (e.g., hide producer selection when embedded in scheduling contact) */
  context?: 'scheduling-contact' | string;
  /** If provided and context is scheduling-contact with forcedType Buyer, preset relationships.producerId */
  presetProducerId?: string;
  /** Optionally override vendorId instead of reading from vendorState */
  vendorId?: string;
  /** Prefix to add to all field names (e.g., 'splitAssignments.0') - makes fields part of parent form */
  prefix?: string;
}

export function CustomerForm({
  customer = null,
  forcedType = undefined,
  onSuccess = undefined,
  onCancel,
  onClose = undefined,
  isInDialog = false,
  showActions = true,
  customActions = undefined,
  idPrefix = 'customer',
  renderAsDiv = false,
  hideAddressSection = false,
  deferMode = false,
  onDeferSubmit,
  context = undefined,
  presetProducerId = undefined,
  vendorId: vendorIdOverride = undefined,
  prefix = undefined,
}: CustomerFormProps) {
  const currentUser = useRecoilValue(userState);
  const vendor = useRecoilValue(vendorState);
  const currentVendorId = vendor?._id;
  const effectiveVendorId = vendorIdOverride ?? currentVendorId;

  // Helper function to get field name with optional prefix
  const getName = (fieldName: string) => {
    return prefix ? `${prefix}.${fieldName}` : fieldName;
  };

  // When prefix is provided, use parent Formik context instead of creating our own
  const parentFormik = prefix ? useFormikContext<any>() : null;

  const {
    formik: ownFormik,
    loading,
    cancelForm,
    shouldShowError: ownShouldShowError,
    fieldProps: ownFieldProps,
  } = useCustomerForm({
    customer,
    forcedType,
    vendorId: effectiveVendorId,
    onSuccess,
    onClose: onClose ?? onCancel,
    deferMode,
    onDeferSubmit,
  });

  // Use parent formik if prefix is provided, otherwise use our own
  const formik = parentFormik || ownFormik;

  // Helper to get nested value from formik values
  const getNestedValue = (path: string) => {
    const keys = path.split('.');
    let value: any = formik.values;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) break;
    }
    return value;
  };

  // Helper to get nested touched/error state
  const getNestedProperty = (obj: any, path: string) => {
    const keys = path.split('.');
    let value: any = obj;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) break;
    }
    return value;
  };

  // Simplified field error checking when using parent context
  const shouldShowError = prefix
    ? (name: string) => {
        const fullName = getName(name);
        return Boolean(
          getNestedProperty(formik.touched, fullName) &&
            getNestedProperty(formik.errors, fullName),
        );
      }
    : ownShouldShowError;

  // Field props helper that works with prefix
  const fieldProps = prefix
    ? (name: string) => {
        const fullName = getName(name);
        return {
          name: fullName,
          value: getNestedValue(fullName) || '',
          onChange: formik.handleChange,
          onBlur: formik.handleBlur,
          error: shouldShowError(name as any),
          helperText: shouldShowError(name as any)
            ? getNestedProperty(formik.errors, fullName)
            : '',
        };
      }
    : ownFieldProps;

  const [addNewProducer, setAddNewProducer] = useState(false);
  const [_pendingProducerText, setPendingProducerText] = useState('');
  const [addressOpen, setAddressOpen] = useState(false);
  const [phoneWarning, setPhoneWarning] = useState<string>('');
  // no-op helper detection removed; we will stabilize layout via fixed height wrapper

  // Get user type options
  const userTypeOptions = getUserTypeOptions(currentUser?.role ?? undefined);

  // Get producers for producer select
  const canLoadProducers = Boolean(
    effectiveVendorId && isInDialog && currentUser && !deferMode,
  );

  const {
    customerOptions: producerOptions,
    refetchCustomers: refetchProducers,
  } = useCustomerOptions(effectiveVendorId, canLoadProducers, {
    relationshipType: EnumUserType.Producer,
  });

  // Get states for state select
  const { data: statesData, loading: statesLoading } = useStateManyQuery({
    skip: deferMode || !currentUser,
  });
  const states = statesData?.stateMany || [];
  const getStatesField = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label="State"
      name="address_state"
      autoComplete="address-level1"
      error={Boolean(
        shouldShowError('address_state') &&
          (formik.errors as any).address_state,
      )}
      helperText={
        shouldShowError('address_state') && (formik.errors as any).address_state
      }
    />
  );

  // When a forced type is provided, keep the form value in sync
  useEffect(() => {
    if (forcedType && (formik.values as any).type !== forcedType) {
      formik.setFieldValue('type', forcedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedType]);

  // Reset producerId when user type changes to non-buyer
  useEffect(() => {
    if (
      (formik.values as any).type !== EnumUserType.Buyer &&
      (formik.values as any).relationships?.producerId
    ) {
      formik.setFieldValue('relationships.producerId', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(formik.values as any).type]);

  // When in scheduling-contact context, preset the producerId and hide selector
  useEffect(() => {
    if (
      context === 'scheduling-contact' &&
      (formik.values as any).type === EnumUserType.Buyer &&
      presetProducerId &&
      !(formik.values as any).relationships?.producerId
    ) {
      formik.setFieldValue('relationships.producerId', presetProducerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, presetProducerId, (formik.values as any).type]);

  // Validate phone number and show warning if invalid
  useEffect(() => {
    const phone = prefix
      ? getNestedValue(getName('phone'))
      : (formik.values as any).phone;
    if (phone && phone.length >= 10) {
      if (!isPhoneNumberValid(phone)) {
        setPhoneWarning('This phone number may be invalid');
      } else {
        setPhoneWarning('');
      }
    } else {
      setPhoneWarning('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefix ? getNestedValue(getName('phone')) : (formik.values as any).phone,
  ]);

  const formContent = (
    <Box
      component={renderAsDiv ? 'div' : 'form'}
      onSubmit={renderAsDiv ? undefined : formik.handleSubmit}
      noValidate={renderAsDiv ? undefined : true}
      role={renderAsDiv ? undefined : 'form'}
      aria-label="Customer form"
    >
      <Box display="grid" gap={2}>
        {!forcedType && (
          <InputSelect
            label="Customer Type"
            nameOverride="type"
            options={userTypeOptions}
            autoSelectFirstOption={false}
            required
            aria-label="Select Customer Type"
          />
        )}
        {(formik.values as any).type === EnumUserType.Buyer &&
          context !== 'scheduling-contact' && (
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                alignItems: { sm: 'center' },
              }}
            >
              <InputTypeahead
                label="Producer"
                nameOverride="relationships.producerId"
                options={producerOptions}
                labelKey="fullName"
                required
                addNoResultsOptionLabel="Add New Producer"
                actions={{
                  addNew: ({ inputValue }) => {
                    setPendingProducerText(inputValue ?? '');
                    setAddNewProducer(true);
                  },
                }}
                aria-label="Select Producer"
              />
              <Button
                variant="outlined"
                content="+ Add"
                onClick={() => {
                  setPendingProducerText('');
                  setAddNewProducer(true);
                }}
                size="lg"
                sx={{
                  alignSelf: { sm: 'start' },
                  minWidth: '120px',
                  width: { xs: '100%', sm: 'auto' },
                  mt: 0.85,
                  ml: { xs: 0, sm: 1 },
                }}
              />
            </Box>
          )}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          <TextField
            label="First name"
            id={`${idPrefix}-first-name`}
            {...fieldProps('first_name')}
            name={getName('first_name')}
            required
            autoComplete="given-name"
            fullWidth
            aria-label="First Name"
            slotProps={{
              input: {
                inputProps: {
                  inputMode: 'text',
                  pattern: '[a-zA-Z]*',
                },
              },
            }}
          />
          <TextField
            label="Last name"
            id={`${idPrefix}-last-name`}
            {...fieldProps('last_name')}
            name={getName('last_name')}
            required
            autoComplete="family-name"
            fullWidth
            aria-label="Last Name"
            slotProps={{
              input: {
                inputProps: {
                  inputMode: 'text',
                  pattern: '[a-zA-Z]*',
                },
              },
            }}
          />
        </Box>
        <Typography
          id={`${idPrefix}-contact-note`}
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0 }}
        >
          * At least one contact method (email or phone) is required
        </Typography>
        <TextField
          label="Phone"
          name={getName('phone')}
          id={`${idPrefix}-phone`}
          value={formatPhoneDisplay(
            prefix
              ? getNestedValue(getName('phone'))
              : (formik.values as any).phone,
          )}
          onChange={(e) => {
            const digits = extractPhoneDigits(e.target.value);
            formik.setFieldValue(getName('phone'), digits);
          }}
          onBlur={formik.handleBlur}
          error={shouldShowError('phone')}
          helperText={
            (shouldShowError('phone') &&
              (prefix
                ? getNestedProperty(formik.errors, getName('phone'))
                : (formik.errors as any).phone)) ||
            (phoneWarning && (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: 'warning.main' }}
              >
                {phoneWarning}
              </Typography>
            )) ||
            undefined
          }
          placeholder="(555) 123-4567"
          autoComplete="tel"
          slotProps={{
            input: {
              inputMode: 'tel',
              'aria-describedby': `${idPrefix}-contact-note`,
            },
          }}
          fullWidth
          aria-label="Phone"
        />
        <TextField
          type="email"
          label="Email"
          id={`${idPrefix}-email`}
          {...fieldProps('email')}
          autoComplete="email"
          slotProps={{
            input: {
              inputMode: 'email',
              'aria-describedby': `${idPrefix}-contact-note`,
            },
          }}
          fullWidth
          aria-label="Email"
        />
        <TextField
          label="Company"
          id={`${idPrefix}-company`}
          {...fieldProps('company')}
          autoComplete="organization"
          fullWidth
          aria-label="Company"
          slotProps={{
            input: {
              inputProps: {
                inputMode: 'text',
                pattern: '[a-zA-Z]*',
              },
            },
          }}
        />
        {!hideAddressSection && (
          <Accordion
            disableGutters
            expanded={addressOpen}
            onChange={(_, isExpanded) => setAddressOpen(isExpanded)}
            sx={{
              boxShadow: 'none',
              m: 0,
              p: 0,
              border: '1px solid #ccc',
              bgcolor: 'transparent',
              backgroundImage: 'none',
              borderRadius: '4px',
              '&.Mui-expanded': { m: 0 },
            }}
          >
            <AccordionSummary
              id={`${idPrefix}-address-summary`}
              aria-controls={`${idPrefix}-address-content`}
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 1,
                bgcolor: 'transparent',
                '&.Mui-expanded': { m: 0, bgcolor: 'transparent' },
              }}
            >
              <Typography variant="body1">
                Address Information{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {addressOpen ? '(click to close)' : '(click to expand)'}
                </Typography>
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              id={`${idPrefix}-address-content`}
              aria-labelledby={`${idPrefix}-address-summary`}
              sx={{ p: 2, pt: 0 }}
            >
              <Box display="grid" gap={2}>
                <TextField
                  label="Street"
                  id={`${idPrefix}-address-street`}
                  {...fieldProps('address_street')}
                  autoComplete="address-line1"
                  fullWidth
                  aria-label="Address"
                  slotProps={{
                    input: {
                      inputProps: {
                        inputMode: 'text',
                        pattern: '[a-zA-Z]*',
                      },
                    },
                  }}
                />
                <TextField
                  label="Address 2 (optional)"
                  id={`${idPrefix}-address-street2`}
                  {...fieldProps('address_street2')}
                  autoComplete="address-line2"
                  fullWidth
                  aria-label="Address 2 (optional)"
                  slotProps={{
                    input: {
                      inputProps: {
                        inputMode: 'text',
                        pattern: '[a-zA-Z]*',
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '2fr 1fr 1fr',
                    },
                  }}
                >
                  <TextField
                    label="City"
                    id={`${idPrefix}-address-city`}
                    {...fieldProps('address_city')}
                    autoComplete="address-level2"
                    fullWidth
                    aria-label="City"
                    slotProps={{
                      input: {
                        inputProps: {
                          inputMode: 'text',
                          pattern: '[a-zA-Z]*',
                        },
                      },
                    }}
                  />
                  <Autocomplete
                    getOptionLabel={(option) => option.name}
                    loading={statesLoading}
                    options={states}
                    filterOptions={(options, { inputValue }) => {
                      const q = inputValue.trim().toLowerCase();
                      if (!q) return options;
                      return options.filter(
                        (o) =>
                          o.name.toLowerCase().includes(q) ||
                          o.code.toLowerCase().includes(q),
                      );
                    }}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    onBlur={formik.handleBlur}
                    onChange={(_, newValue) => {
                      formik.setFieldValue(
                        'address_state',
                        newValue?.code || '',
                      );
                    }}
                    renderInput={(params) => getStatesField(params)}
                    renderOption={(props, option) => (
                      <li {...props}>{option.name}</li>
                    )}
                    value={
                      states.find(
                        (s) => s.code === (formik.values as any).address_state,
                      ) || null
                    }
                  />
                  <TextField
                    label="ZIP"
                    id={`${idPrefix}-address-postcode`}
                    {...fieldProps('address_postcode')}
                    autoComplete="postal-code"
                    slotProps={{
                      input: {
                        inputProps: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        },
                      },
                    }}
                    fullWidth
                    aria-label="ZIP"
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
      {addNewProducer && (
        <NestedProducerDialog
          open={addNewProducer}
          onClose={() => setAddNewProducer(false)}
          onSuccess={async (created) => {
            await refetchProducers?.();
            formik.setFieldValue('relationships.producerId', created._id);
            setAddNewProducer(false);
          }}
        />
      )}
      {showActions && (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}
        >
          {customActions || (
            <>
              <Button variant="text" onClick={cancelForm} content="Cancel" />
              <Button
                type={renderAsDiv ? 'button' : 'submit'}
                onClick={renderAsDiv ? () => formik.handleSubmit() : undefined}
                variant="contained"
                isLoading={formik.isSubmitting || loading}
                content="Save"
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );

  // Only wrap in FormikProvider if we're not using the parent context
  return prefix ? (
    formContent
  ) : (
    <FormikProvider value={formik}>{formContent}</FormikProvider>
  );
}
